import { useState, useMemo, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'
import { FlowArrowIcon, MagicWandIcon, RobotIcon, TreeStructureIcon } from '@phosphor-icons/react'
import { ML } from './ML/ML/ML'
import { Server } from '../../../api/Server'
import MLConfig from './ML/MLConfig'
import { useWorkspaceMetadata } from '../../../api/hooks/workspaceMetadata/useWorkspaceMetadata'
import { EmptyDataset } from '../../EmptyDataset/EmptyDataset'
import { ROUTES } from '../../../router/routes'
import { Process } from './Process/Process'
import { SocketChat } from '../SocketChat/SocketChat'
import { ScoopText, ScoopRow } from '../../ui'
import { useIsTourWorkspace } from '../../../hooks/useIsTourWorkspace'
import styles from './Insights.module.scss'

// Keep the export for other files to use
export var insightTabs = [
    { label: 'Ask Scoop', key: 'ask-ai', icon: <MagicWandIcon size={24} /> },
    {
        label: 'Data Science Discovery Studio',
        key: 'machine-learning',
        icon: <TreeStructureIcon size={24} color="#ffffff" />,
    },
    { label: 'Process Analysis', key: 'processes', icon: <FlowArrowIcon size={24} color="#FFF" /> },
]

export const Insights = () => {
    const isTourWorkspace = useIsTourWorkspace()
    const workspaceID = useSelector((state) => state.auth.workspaceID)
    const userID = useSelector((state) => state.auth.userID)
    const token = useSelector((state) => state.auth.token)
    const workspaceName = useSelector((state) => state.auth.workspaceName)
    const { state } = useLocation()
    const navigate = useNavigate()
    const { workspaceMetadata, isLoading } = useWorkspaceMetadata()

    // Create filtered tabs for internal use based on account type
    const availableTabs = useMemo(() => {
        const accountType = workspaceMetadata?.account?.accountType

        // AppSumo Tier 3 gets all tabs including Process Analysis
        if (accountType === 'appsumo_team_unlimited') {
            return insightTabs // All tabs
        }

        // AppSumo Tier 1 & 2 and Tour Scoop only get Ask Scoop
        if (accountType?.startsWith('appsumo') || workspaceName === 'Tour Scoop') {
            return [insightTabs[0]] // Only "Ask Scoop" tab
        }

        return insightTabs // All tabs for free trial and paid account types
    }, [workspaceMetadata, workspaceName])

    // Initialize selectedTab with validation
    const [selectedTab, setSelectedTab] = useState(() => {
        const initialTab = state?.selectedTab || availableTabs[0].key
        // Validate that the initial tab exists in the available tabs
        const tabExists = availableTabs.some((tab) => tab.key === initialTab)
        return tabExists ? initialTab : availableTabs[0].key
    })

    const [selectedInbox, setSelectedInbox] = useState('')
    const [mlResults, setMLResults] = useState()
    const [mlConfig, setMlConfig] = useState(new MLConfig())
    const [chatResult, setChatResult] = useState()
    const [fieldList, setFieldList] = useState([])
    const [filterList, setFilterList] = useState([])

    // Select the right inbox when workspace metadata loads.
    // Priority: 1) createdInbox from nav state (if it exists in this workspace)
    //           2) saved inbox from localStorage
    //           3) first inbox with data
    useEffect(() => {
        if (selectedInbox || !workspaceMetadata?.inboxes?.length) return

        const inboxes = workspaceMetadata.inboxes
        const hasData = (inbox) => inbox.tables?.some((table) => table.columns?.length > 0)

        // 1) If nav state has a createdInbox, use it only if it belongs to this workspace
        if (state?.createdInbox) {
            const match = inboxes.find(
                (inbox) => inbox.inboxID === state.createdInbox.inboxID && hasData(inbox)
            )
            if (match) {
                setInbox(match)
                return
            }
        }

        // 2) Try to restore saved inbox from localStorage
        const storageKey = `scoop_insights_${userID}_${workspaceID}_selectedInboxID`
        const savedInboxID = localStorage.getItem(storageKey)
        if (savedInboxID) {
            const saved = inboxes.find((inbox) => inbox.inboxID === savedInboxID && hasData(inbox))
            if (saved) {
                setInbox(saved)
                return
            }
        }

        // 3) Fall back to first inbox with data
        const first = inboxes.find(hasData)
        if (first) setInbox(first)
    }, [workspaceMetadata, selectedInbox, state, userID, workspaceID])

    // Update selectedTab if it becomes invalid due to account type change
    useEffect(() => {
        const tabStillExists = availableTabs.some((tab) => tab.key === selectedTab)
        if (!tabStillExists && availableTabs.length > 0) {
            setSelectedTab(availableTabs[0].key)
        }
    }, [availableTabs, selectedTab])

    const setInbox = (newInbox) => {
        setColumns(newInbox)
        mlConfig.config.selectedItems = ['All']
        mlConfig.config.timeRange = 'All'
        mlConfig.config.view = 'table'
        mlConfig.selectedFilters = null
        if (newInbox?.tables?.[0]?.dates?.length > 0) {
            mlConfig.filterDate = newInbox.tables[0].dates[0]
            setMlConfig({ ...mlConfig })
        }
        const server = new Server(workspaceID, userID, token)
        server.postData(
            {
                action: 'getInboxMetadata',
                inboxID: newInbox.inboxID,
                workspaceID: workspaceID,
            },
            (result) => {
                if (result.savedFilters && result.savedFilters.length > 0) {
                    let newFilterList = []
                    for (let i = 0; i < result.savedFilters.length; i++) {
                        newFilterList.push({
                            id: result.savedFilters[i].key,
                            value: result.savedFilters[i].name,
                        })
                    }
                    setFilterList(newFilterList)
                } else {
                    setFilterList([])
                }
            }
        )
        localStorage.setItem(
            `scoop_insights_${userID}_${workspaceID}_selectedInboxID`,
            newInbox.inboxID
        )
        setSelectedInbox(newInbox)
    }

    function setColumns(inbox) {
        let newFieldList = []
        let newSelectedFields = []
        // Add safety check for tables and columns
        if (inbox?.tables?.[0]?.columns?.length > 0) {
            for (let i = 0; i < inbox.tables[0].columns.length; i++) {
                let colName = inbox.tables[0].columns[i].columnName
                newFieldList.push({ id: colName, value: colName })
                newSelectedFields.push(colName)
            }
        }
        setFieldList(newFieldList)
        mlConfig.selectedFields = newSelectedFields
        setMlConfig({ ...mlConfig })
    }

    const shouldRenderScreen = () => {
        if (workspaceMetadata) {
            let inboxesHaveColumns = false
            workspaceMetadata.inboxes.forEach((inbox) => {
                if (inbox.tables?.some((table) => table.columns?.length)) inboxesHaveColumns = true
            })
            let currentInboxHasColumns = true
            if (selectedInbox) {
                const inbox = workspaceMetadata.inboxes?.filter(
                    (i) => i.inboxID === selectedInbox.inboxID
                )[0]
                if (inbox) {
                    currentInboxHasColumns = inbox?.tables?.some((table) => table.columns?.length)
                }
            }
            return inboxesHaveColumns && currentInboxHasColumns
        }
        return false
    }

    const handleGoToDataset = () => {
        navigate(ROUTES.SOURCES, { state: { openNewDataset: true } })
    }

    return (
        <div className={styles.screenContainer}>
            {!isLoading &&
                (shouldRenderScreen() ? (
                    <>
                        <div className={styles.explorerNav}>
                            <ScoopRow align="center">
                                {availableTabs.map((tab, index) => (
                                    <div
                                        onClick={() => setSelectedTab(tab.key)}
                                        key={`${tab.key}-${index}`}
                                        className={`${styles.tabItem} ${selectedTab === tab.key ? styles.active : ''}`}
                                    >
                                        {tab.icon}
                                        <ScoopText className={styles.tabLabel}>
                                            {tab.label}
                                        </ScoopText>
                                    </div>
                                ))}
                            </ScoopRow>
                        </div>
                        <div className={styles.explorerContent}>
                            {selectedTab === 'ask-ai' && (
                                <SocketChat
                                    selectedInbox={selectedInbox}
                                    setSelectedInbox={setInbox}
                                    setSelectedTab={setSelectedTab}
                                    mlConfig={mlConfig}
                                    setMlConfig={setMlConfig}
                                    setMLResults={setMLResults}
                                    isTourWorkspace={isTourWorkspace}
                                />
                            )}
                            {selectedTab === 'machine-learning' &&
                                availableTabs.some((t) => t.key === 'machine-learning') && (
                                    <ML
                                        selectedInbox={selectedInbox}
                                        setSelectedInbox={setInbox}
                                        selectedTab={selectedTab}
                                        setSelectedTab={setSelectedTab}
                                        mlResults={mlResults}
                                        setMLResults={setMLResults}
                                        chatResult={chatResult}
                                        mlConfig={mlConfig}
                                        setMlConfig={setMlConfig}
                                        fieldList={fieldList}
                                        filterList={filterList}
                                    />
                                )}
                            {selectedTab === 'processes' &&
                                availableTabs.some((t) => t.key === 'processes') && (
                                    <Process processDiagramToEdit={state?.processDiagramToEdit} />
                                )}
                        </div>
                    </>
                ) : (
                    <div className={styles.emptyContainer}>
                        <EmptyDataset
                            title={'Welcome to AI Insights'}
                            description={
                                "Ask questions and let Scoop's AI chatbot explore your data for instant answers! Our AI will discover segments, patterns, and predictors in your dataset"
                            }
                            iconComponent={<RobotIcon size={98} />}
                            onClick={handleGoToDataset}
                        />
                    </div>
                ))}
        </div>
    )
}
